// BBB-517: Scotland boundary polygon, used only to arbitrate the
// England/Scotland bounding-box overlap in classifyPoint (see
// useViewportRegion.js). Sibling of walesBoundary.js — same source, same
// licence, same reason for existing.
//
// Simplification measurements, grid verification and probe derivation:
// docs/boundary-classification.md.
//
// ⚠️ CLIPPED — read this before using it for anything else
// Unlike WALES_BOUNDARY, this is NOT the whole country. It is clipped to
// SCOTLAND_CLIP_BBOX below, which contains the corridor where the Scotland and
// England bboxes overlap and nothing else. Clipping drops ~20 kB gzip of
// Hebrides, Orkney and Shetland coastline that can never be inside that
// overlap.
//
// The consequence is that this polygon answers "false" for most of Scotland —
// Inverness, Aberdeen, Skye and everything north of 55.95 is outside the clip.
// It is therefore ONLY safe to consult for points that could also be England.
// classifyPoint enforces that with its `contested` gate; anything else that
// reaches for this constant must do the same, or it will send most of Scotland
// to out-of-coverage. useViewportRegion.test.js asserts the containment
// invariant so widening a bbox cannot break this silently.
//
// SOURCE, LICENCE, RETRIEVAL
// Source: ONS Open Geography Portal, "Countries (December 2024) Boundaries UK
// BUC" (Ultra Generalised, 500m, clipped to coastline), Scotland feature only.
//   https://open-geography-portalx-ons.hub.arcgis.com/datasets/ons::countries-december-2024-boundaries-uk-buc-2
// Retrieved 11/08/2026 directly from the underlying ArcGIS FeatureServer:
//   https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/Countries_December_2024_Boundaries_UK_BUC/FeatureServer/0/query?where=CTRY24NM=%27Scotland%27&outFields=CTRY24CD,CTRY24NM&outSR=4326&f=geojson
// CRITICAL: the request above MUST include `outSR=4326` — see the same note in
// walesBoundary.js. Verified on retrieval: 8,371 points, lng -8.65 to -0.7485,
// lat 54.6333 to 60.8609, all inside plausible UK ranges.
// Licence: Open Government Licence v3.0.
// Attribution (per https://www.ons.gov.uk/methodology/geography/licences):
// Contains OS data (c) Crown copyright and database right 2024. Contains ONS
// data (c) Crown copyright and database right 2024. Source: Office for
// National Statistics licensed under the Open Government Licence v3.0.
//
// That attribution is a licence obligation and was a comment only, on no screen
// anywhere, until the map credits were built. The rendered copy of it is
// ONS_OS_BOUNDARY_ATTRIBUTION in data/attributions.js, and attributions.test.js
// asserts the constant and this comment cannot drift apart. This header stays
// because it is the provenance record and carries the retrieval URLs; do not
// delete the string from it.
//
// Clipped and simplified with
// `mapshaper -clip bbox=-6.5,54.5,-0.6,55.95 -simplify 70% keep-shapes`, 4dp.
import SCOTLAND_BOUNDARY_GEOMETRY from './scotlandBoundary.json';

/**
 * The bbox SCOTLAND_BOUNDARY was clipped to.
 *
 * Exported so the clip's containment invariant is machine-checked rather than
 * asserted in prose across two files: it MUST contain the England/Scotland bbox
 * overlap, or points in the uncovered part of that overlap silently fall through
 * to England. See useViewportRegion.test.js.
 */
export const SCOTLAND_CLIP_BBOX = { west: -6.5, south: 54.5, east: -0.6, north: 55.95 };

export const SCOTLAND_BOUNDARY = SCOTLAND_BOUNDARY_GEOMETRY;
